import { describeLinkForLog, isSameDocumentAddress } from '@hibro/shared';

/**
 * Keep the workbench document from being navigated away, and route the address
 * it was leaving for through `openLink` instead (见 20 外链归属路由 / 06).
 *
 * Nothing in a page can intercept `location.href = …`: `window.location` and
 * `Location`'s own members are `[LegacyUnforgeable]`, non-configurable by design
 * so that a page cannot spoof or trap navigation. The Navigation API is the door
 * the browser itself opens — its `navigate` event fires for `location.href`,
 * `location.replace`, anchor clicks, form submits and `<meta http-equiv=refresh>`
 * alike, and cancelling it is allowed even when the destination is cross-origin
 * (`canIntercept: false` only withdraws the right to *take over* rendering, which
 * this guard never wants).
 *
 * **Why the routing decision is safe here and not in the main process's
 * backstop** (见 20 §权限面): `openLink` may deliver an address to the module that
 * claims it, which presents that module's view — a capability the sandbox must
 * not reach. This listener runs in the workbench realm, and on the desktop a
 * module `iframe` is a cross-origin `hibro-assets://<moduleId>` document without
 * `allow-top-navigation`, so it cannot cause a navigation this event would see.
 * Code that *can* is trusted source code in this realm, which could simply have
 * called `openLink` itself. `setWindowOpenHandler` gets no such guarantee (its
 * details carry no initiating frame at all), which is why that path still only
 * falls back to the built-in browser.
 *
 * On web the same-origin module `iframe` can reach this realm (见 18 「同源 iframe
 * 的 sandbox 实际失效」), so the delivery decision there rests on that already
 * accepted limitation rather than on isolation.
 */
export interface NavigationGuardOptions {
  /**
   * Trusted renderer `openLink` — the same three-answer route a source view's
   * `ViewProps.openLink` takes (deep link, owning module, runtime presentation).
   */
  openLink: (url: string) => Promise<boolean>;
  /** Shell-level diagnostic sink (见 15). */
  log?: (message: string) => void;
}

/** What {@link shouldBlockNavigation} needs to know about a pending navigation. */
export interface PendingNavigation {
  /** `NavigateEvent.destination.url`. */
  readonly destinationUrl: string;
  /** `NavigateEvent.destination.sameDocument`. */
  readonly sameDocument: boolean;
  /** Whether this is a download navigation (`downloadRequest !== null`). */
  readonly isDownload: boolean;
  /** The address of the document the guard is protecting. */
  readonly documentUrl: string;
  /**
   * One-shot leave the page asked for via {@link allowDocumentNavigation} /
   * {@link navigateAway}. Compared as a resolved href, so query is significant
   * (OAuth `state` is part of the identity of that hop).
   */
  readonly allowedUrl?: string;
}

/**
 * Whether this navigation must be blocked and re-routed.
 *
 * Three things pass through untouched, and each is a real case rather than a
 * courtesy:
 *
 * - **Same-document navigations** — `location.hash`, `?query` changes and
 *   `history.pushState`/`replaceState` (the last two do fire the event, measured
 *   in `.test_tools/nav-api-spike`). The document survives, so there is nothing
 *   to protect.
 * - **Downloads** — a `<a download>` navigation never replaces the document. The
 *   web runtime's own `saveStorageFile` uses a hidden `iframe` navigation (见 18),
 *   which is a subframe and never reaches this listener at all.
 * - **The workbench's own address** — `location.reload()` reports
 *   `sameDocument: false` with an unchanged URL, so without this the guard would
 *   make the app unreloadable.
 * - **An explicit leave** — web login must replace this tab so the IdP can
 *   return to `redirect_uri` on the same browsing context (见 18 / 20). That is
 *   the opposite of `openLink`, which presents an address *without* leaving.
 */
export function shouldBlockNavigation(pending: PendingNavigation): boolean {
  if (pending.sameDocument || pending.isDownload) {
    return false;
  }
  if (
    pending.allowedUrl &&
    sameResolvedHref(pending.destinationUrl, pending.allowedUrl)
  ) {
    return false;
  }
  return !isSameDocumentAddress(pending.destinationUrl, pending.documentUrl);
}

/**
 * Resolved href, or `undefined` when the input is not a URL. Used only to
 * compare two addresses the page already decided to visit — never to log.
 */
function canonicalHref(url: string): string | undefined {
  try {
    return new URL(url).href;
  } catch {
    return undefined;
  }
}

function sameResolvedHref(left: string, right: string): boolean {
  const a = canonicalHref(left);
  const b = canonicalHref(right);
  return a !== undefined && a === b;
}

/** One-shot destination the next `navigate` event may replace this document with. */
let pendingAllow: string | undefined;

/**
 * Permit the next navigation to `url` to replace this document (见 20).
 *
 * Consumed on the first matching `navigate` event. A different destination
 * does not spend it, so a stray hop cannot burn a login leave. Unparseable
 * input allows nothing.
 */
export function allowDocumentNavigation(url: string): void {
  pendingAllow = canonicalHref(url);
}

/** Drop a leave that was never consumed. */
export function clearDocumentNavigationAllow(): void {
  pendingAllow = undefined;
}

/**
 * Replace this document with `url` — the page-side half of web OAuth (见 18).
 *
 * Distinct from `openLink` (present the address, keep the workbench) and
 * `openExternal` (open outside the app). Desktop login does not use this: it
 * opens the system browser, and the main-process `will-navigate` backstop would
 * still refuse a real leave.
 */
export function navigateAway(url: string): void {
  allowDocumentNavigation(url);
  window.location.assign(url);
}

/**
 * Install the guard on this window; returns a disposer.
 *
 * Absent `window.navigation` (a browser older than the API's Baseline) leaves the
 * page unguarded and says so in the log rather than substituting a click-capture
 * approximation: anchors are only one of the ways out, so a partial guard would
 * read as protection the page does not have. The desktop still has the main
 * process backstop.
 */
export function installNavigationGuard(
  options: NavigationGuardOptions,
): () => void {
  const { openLink, log } = options;
  const api = window.navigation as Navigation | undefined;
  if (!api) {
    log?.(
      'navigation guard: this browser has no Navigation API, so a module that ' +
        'navigates the top document can still replace the workbench',
    );
    return () => undefined;
  }
  const onNavigate = (event: NavigateEvent): void => {
    const target = event.destination.url;
    const allowedUrl = pendingAllow;
    if (allowedUrl && sameResolvedHref(target, allowedUrl)) {
      pendingAllow = undefined;
    }
    if (
      !shouldBlockNavigation({
        destinationUrl: target,
        sameDocument: event.destination.sameDocument,
        isDownload: event.downloadRequest !== null,
        documentUrl: window.location.href,
        ...(allowedUrl ? { allowedUrl } : {}),
      })
    ) {
      return;
    }
    event.preventDefault();
    const link = describeLinkForLog(target);
    openLink(target).then(
      (taken) => {
        log?.(
          `navigation guard: kept the workbench and routed ${link} through openLink (taken by a module: ${taken})`,
        );
      },
      (error: unknown) => {
        log?.(
          `navigation guard: kept the workbench but openLink failed for ${link}: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      },
    );
  };
  api.addEventListener('navigate', onNavigate);
  return () => {
    pendingAllow = undefined;
    api.removeEventListener('navigate', onNavigate);
  };
}
